<template>
  <div class="feedback-list-container">
    <h2>评价与反馈列表</h2>
    <el-card shadow="hover">
      <div class="card-header">
        <el-input
          v-model="searchQuery"
          placeholder="搜索用户名称或反馈内容"
          style="width: 300px; margin-right: 10px;"
          clearable
        >
          <template #prefix>
            <el-icon><search /></el-icon>
          </template>
        </el-input>
        <el-select v-model="statusFilter" placeholder="筛选状态" style="width: 150px; margin-right: 10px;">
          <el-option label="全部" value="" />
          <el-option label="待处理" value="unprocessed" />
          <el-option label="已处理" value="processed" />
        </el-select>
        <el-button type="primary" @click="handleSearch">搜索</el-button>
      </div>
      
      <el-table :data="feedbacks" border stripe style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="name" label="用户名称" />
        <el-table-column prop="type" label="类型" width="100">
          <template #default="scope">
            <el-tag :type="scope.row.type === 'complaint' ? 'danger' : scope.row.type === 'suggestion' ? 'warning' : 'info'">
              {{ scope.row.type === 'complaint' ? '投诉' : scope.row.type === 'suggestion' ? '建议' : '评价' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="content" label="内容" :show-overflow-tooltip="true" width="300" />
        <el-table-column prop="status" label="状态" width="100">
          <template #default="scope">
            <el-tag :type="scope.row.status === 'processed' ? 'success' : 'warning'">
              {{ scope.row.status === 'processed' ? '已处理' : '待处理' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="提交时间" width="180" />
        <el-table-column label="操作" width="250" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="handleViewDetails(scope.row)">
              <el-icon><View /></el-icon> 查看详情
            </el-button>
            <el-button 
              type="success" 
              size="small" 
              @click="handleProcess(scope.row)" 
              v-if="scope.row.status !== 'processed'"
            >
              处理
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="card-footer">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
    
    <!-- 反馈详情抽屉 -->
    <el-drawer
      v-model="drawerVisible"
      title="评价与反馈详情"
      direction="rtl"
      size="60%"
    >
      <div v-if="currentFeedback" class="feedback-detail">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="ID">{{ currentFeedback.id }}</el-descriptions-item>
          <el-descriptions-item label="用户名称">{{ currentFeedback.name }}</el-descriptions-item>
          <el-descriptions-item label="用户电话">{{ currentFeedback.phone }}</el-descriptions-item>
          <el-descriptions-item label="反馈类型">
            <el-tag :type="currentFeedback.type === 'complaint' ? 'danger' : currentFeedback.type === 'suggestion' ? 'warning' : 'info'">
              {{ currentFeedback.type === 'complaint' ? '投诉' : currentFeedback.type === 'suggestion' ? '建议' : '评价' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="反馈内容">{{ currentFeedback.content }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="currentFeedback.status === 'processed' ? 'success' : 'warning'">
              {{ currentFeedback.status === 'processed' ? '已处理' : '待处理' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="提交时间">{{ currentFeedback.created_at }}</el-descriptions-item>
        </el-descriptions>
        
        <!-- 图片展示 -->
        <div v-if="currentFeedback.images && currentFeedback.images.length > 0" class="feedback-images">
          <h3>图片附件</h3>
          <div class="image-list">
            <el-image
              v-for="(image, index) in currentFeedback.images"
              :key="index"
              :src="image"
              :preview-src-list="currentFeedback.images"
              fit="cover"
              style="width: 100px; height: 100px; margin-right: 10px; margin-bottom: 10px;"
            />
          </div>
        </div>
        
        <div class="drawer-footer">
          <el-button 
            type="success" 
            @click="handleProcess(currentFeedback)" 
            :disabled="currentFeedback.status === 'processed'"
          >
            处理
          </el-button>
          <el-button @click="drawerVisible = false">关闭</el-button>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { View } from '@element-plus/icons-vue';
import request from '../../api/axios';

export default {
  name: 'FeedbackList',
  components: {
    View
  },
  setup() {
    const feedbacks = ref([]);
    const searchQuery = ref('');
    const statusFilter = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const loading = ref(false);
    
    // 抽屉相关变量
    const drawerVisible = ref(false);
    const currentFeedback = ref(null);
    
    // 获取评价与反馈列表
    const fetchFeedbacks = async () => {
      loading.value = true;
      try {
        const response = await request({
          url: '/manage/feedback',
          method: 'get',
          params: {
            page: currentPage.value,
            pageSize: pageSize.value,
            search: searchQuery.value,
            status: statusFilter.value
          }
        });
        // 后端返回的是嵌套数据结构，需要正确提取
        feedbacks.value = response.data.data;
        total.value = response.data.total;
      } catch (error) {
        ElMessage.error('获取评价与反馈列表失败');
        console.error(error);
      } finally {
        loading.value = false;
      }
    };
    
    // 页面加载时获取评价与反馈列表
    onMounted(() => {
      fetchFeedbacks();
    });
    
    // 搜索
    const handleSearch = () => {
      currentPage.value = 1;
      fetchFeedbacks();
    };
    
    // 查看评价与反馈详情 - 使用抽屉展示
    const handleViewDetails = (feedback) => {
      currentFeedback.value = feedback;
      drawerVisible.value = true;
    };
    
    // 处理评价与反馈
    const handleProcess = async (feedback) => {
      try {
        await request({
          url: `/manage/feedback/${feedback.id}/process`,
          method: 'post'
        });
        ElMessage.success('处理成功');
        fetchFeedbacks();
        // 如果当前正在查看该反馈的详情，更新状态
        if (currentFeedback.value && currentFeedback.value.id === feedback.id) {
          currentFeedback.value.status = 'processed';
        }
      } catch (error) {
        ElMessage.error('处理失败');
        console.error(error);
      }
    };
    
    // 处理分页大小变化
    const handleSizeChange = (size) => {
      pageSize.value = size;
      fetchFeedbacks();
    };
    
    // 处理当前页变化
    const handleCurrentChange = (current) => {
      currentPage.value = current;
      fetchFeedbacks();
    };
    
    // 获取状态类型
    const getStatusType = (status) => {
      const statusMap = {
        'unprocessed': 'warning',
        'processed': 'success'
      };
      return statusMap[status] || 'info';
    };
    
    // 获取状态名称
    const getStatusName = (status) => {
      const statusMap = {
        'unprocessed': '待处理',
        'processed': '已处理'
      };
      return statusMap[status] || status;
    };
    
    return {
      feedbacks,
      searchQuery,
      statusFilter,
      currentPage,
      pageSize,
      total,
      loading,
      drawerVisible,
      currentFeedback,
      handleSearch,
      handleViewDetails,
      handleProcess,
      handleSizeChange,
      handleCurrentChange,
      getStatusType,
      getStatusName
    };
  }
};
</script>

<style scoped>
.feedback-list-container {
  padding: 20px;
}

.feedback-list-container h2 {
  margin-bottom: 20px;
  color: #333;
}

.card-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 20px;
}

.card-footer {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 抽屉样式 */
.feedback-detail {
  padding: 20px 0;
}

.feedback-detail h3 {
  margin: 20px 0 10px 0;
  color: #333;
  font-size: 16px;
  font-weight: bold;
}

.feedback-images {
  margin-top: 20px;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;
}

.drawer-footer {
  margin-top: 30px;
  display: flex;
  justify-content: flex-end;
}
</style>